import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { db, SYSTEM_TEMPLATE_ORG_ID, transactWithRetry, ADMIN_ORG_ID, batchTransact } from '@/lib/db'
import { id } from '@instantdb/react'
import { toast } from 'sonner'
import { Plus, Loader2, Calendar, Trash2, Eye, Sparkles, LayoutGrid, Copy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import type { Project } from '@/types'
import { CreateProjectDialog } from '@/components/business/CreateProjectDialog'
import { useNoteStyles } from '@/hooks/useNoteStyles'
import { useProfile } from '@/hooks/useProfile'
import { NoteStyleEditor } from '@/components/business/NoteStyleEditor'

export function TemplateWorkbenchPage() {
  const navigate = useNavigate()
  const { profile, isLoading: isProfileLoading } = useProfile()
  const [searchQuery, setSearchQuery] = useState('')
  const toastShownRef = useRef(false)
  
  // Style management state
  const [styleEditorOpen, setStyleEditorOpen] = useState(false)
  const [currentStyleId, setCurrentStyleId] = useState<string | null>(null)
  const [isCreatingStyle, setIsCreatingStyle] = useState(false)
  const [isCloning, setIsCloning] = useState<Record<string, boolean>>({})

  const { 
    styles, 
    styleMap, 
    templateMap, 
    imageSlots,
    copywritingStyleMap,
    deleteStyle, 
    isLoading: isStylesLoading 
  } = useNoteStyles(SYSTEM_TEMPLATE_ORG_ID)

  // 权限检查
  useEffect(() => {
    if (!isProfileLoading && profile && profile.org_id !== ADMIN_ORG_ID) {
      if (!toastShownRef.current) {
        toast.error('您没有权限访问此页面')
        toastShownRef.current = true
      }
      navigate('/', { replace: true })
    }
  }, [profile, isProfileLoading, navigate])

  // 查询系统模板企业下的所有项目
  const { data, isLoading: isDataLoading } = db.useQuery({
    projects: {
      $: {
        where: { org_id: SYSTEM_TEMPLATE_ORG_ID },
      },
    },
  })

  const isLoading = isProfileLoading || isDataLoading || isStylesLoading

  if (!isProfileLoading && profile?.org_id !== ADMIN_ORG_ID) {
    return null
  }

  const templateProjects = (data?.projects || []) as Project[]

  // 过滤搜索
  const filteredProjects = templateProjects.filter((project) => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      project.title.toLowerCase().includes(query) ||
      project.description?.toLowerCase().includes(query)
    )
  })

  const handleCreateStyleTemplate = async () => {
    if (isCreatingStyle) return
    setIsCreatingStyle(true)
    try {
      const newId = id()
      await transactWithRetry(
        db.tx.note_styles[newId].update({
          id: newId,
          org_id: SYSTEM_TEMPLATE_ORG_ID,
          name: '新风格模板',
          description: '请完善模板描述',
          reference_images: [],
          created_at: Date.now(),
        })
      )
      toast.success('已创建风格模板')
      setCurrentStyleId(newId)
      setStyleEditorOpen(true)
    } catch (error) {
      console.error(error)
      toast.error('创建失败')
    } finally {
      setIsCreatingStyle(false)
    }
  }

  const handleDeleteTemplate = async (projectId: string, projectTitle: string) => {
    if (!confirm(`确定要删除模板「${projectTitle}」吗？\n\n此操作不可恢复！`)) {
      return
    }

    try {
      await transactWithRetry(db.tx.projects[projectId].delete())
      toast.success('模板已删除')
    } catch (error) {
      console.error(error)
      toast.error('删除失败，请稍后重试')
    }
  }

  const handleViewTemplate = (projectId: string) => {
    navigate(`/project/${projectId}`)
  }

  const handleCloneStyle = async (styleId: string, styleName: string) => {
    const targetOrgId = '465961a6-8134-4566-a0bb-44d1225cddeb'
    
    setIsCloning(prev => ({ ...prev, [styleId]: true }))
    try {
      const style = styleMap[styleId]
      if (!style) throw new Error('未找到风格数据')

      const txns: any[] = []
      const newStyleId = id()
      const templateIdMap: Record<string, string> = {}

      // 1. 克隆风格主体
      txns.push(db.tx.note_styles[newStyleId].update({
        ...style,
        id: newStyleId,
        org_id: targetOrgId,
        created_at: Date.now(),
      }))

      // 2. 克隆文案风格
      const styleCopywritingStyles = copywritingStyleMap[styleId] || []
      styleCopywritingStyles.forEach(cpy => {
        const newCpyId = id()
        txns.push(db.tx.copywriting_styles[newCpyId].update({
          ...cpy,
          id: newCpyId,
          style_id: newStyleId,
          org_id: targetOrgId,
          created_at: Date.now(),
        }))
      })

      // 3. 克隆模板
      const styleTemplates = templateMap[styleId] || []
      styleTemplates.forEach(tpl => {
        const newTplId = id()
        templateIdMap[tpl.id] = newTplId
        txns.push(db.tx.note_templates[newTplId].update({
          ...tpl,
          id: newTplId,
          style_id: newStyleId,
          org_id: targetOrgId,
          created_at: Date.now(),
        }))
      })

      // 4. 克隆槽位
      const styleTemplateIds = styleTemplates.map(t => t.id)
      const styleSlots = imageSlots.filter(s => styleTemplateIds.includes(s.template_id))
      styleSlots.forEach(slot => {
        const newSlotId = id()
        const newTplId = templateIdMap[slot.template_id]
        if (newTplId) {
          txns.push(db.tx.note_image_slots[newSlotId].update({
            ...slot,
            id: newSlotId,
            template_id: newTplId,
            org_id: targetOrgId,
            created_at: Date.now(),
          }))
        }
      })

      await batchTransact(txns)
      toast.success(`风格「${styleName}」已复制到企业`)
    } catch (error) {
      console.error(error)
      toast.error('复制失败')
    } finally {
      setIsCloning(prev => ({ ...prev, [styleId]: false }))
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold">模板工作台</h1>
        <p className="text-muted-foreground text-sm">
          管理系统级初始化模板。在这里维护的项目和风格，会在新账号加入企业时自动克隆。
        </p>
      </div>

      <Tabs defaultValue="projects" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
          <TabsTrigger value="projects" className="gap-2">
            <LayoutGrid className="w-4 h-4" />
            项目模板
          </TabsTrigger>
          <TabsTrigger value="styles" className="gap-2">
            <Sparkles className="w-4 h-4" />
            风格模板
          </TabsTrigger>
        </TabsList>

        <TabsContent value="projects" className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                placeholder="搜索项目模板..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-sm"
              />
            </div>
            <CreateProjectDialog 
              buttonText="新建项目模板" 
              overrideOrgId={SYSTEM_TEMPLATE_ORG_ID} 
            />
          </div>

          {isLoading ? (
            <div className="h-64 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : filteredProjects.length === 0 ? (
            <Card className="p-8 border-dashed text-center space-y-3">
              <div className="flex justify-center">
                <Plus className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">
                {searchQuery ? '未找到匹配的模板' : '暂无模板项目，点击「新建模板」开始创建'}
              </p>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredProjects.map((project) => {
                const styleInfo = project.note_style_id ? styleMap[project.note_style_id] : undefined
                return (
                  <Card key={project.id} className="shadow-sm border hover:shadow-md transition-shadow">
                    <CardHeader>
                      <div className="space-y-3">
                        <CardTitle className="text-lg line-clamp-1">{project.title}</CardTitle>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {project.description || '暂无描述'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {styleInfo && (
                            <Badge variant="outline" className="text-xs">
                              {styleInfo.name}
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-xs gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(project.created_at).toLocaleDateString('zh-CN')}
                          </Badge>
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 gap-2"
                            onClick={() => handleViewTemplate(project.id)}
                          >
                            <Eye className="w-4 h-4" />
                            查看内容
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/20"
                            onClick={() => handleDeleteTemplate(project.id, project.title)}
                          >
                            <Trash2 className="w-4 h-4" />
                            删除
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                )
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="styles" className="space-y-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              管理系统初始化时提供的笔记风格。新企业加入时会克隆这些风格。
            </p>
            <Button onClick={handleCreateStyleTemplate} disabled={isCreatingStyle} className="gap-2">
              {isCreatingStyle ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              新建风格模板
            </Button>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {styles.map((style) => (
              <Card key={style.id} className="shadow-sm border">
                <CardHeader>
                  <CardTitle className="text-lg">{style.name}</CardTitle>
                  <p className="text-sm text-muted-foreground line-clamp-2">{style.description}</p>
                </CardHeader>
                <CardContent className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 gap-2"
                    onClick={() => {
                      setCurrentStyleId(style.id)
                      setStyleEditorOpen(true)
                    }}
                  >
                    <Eye className="w-4 h-4" />
                    编辑模板
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="gap-2"
                    disabled={isCloning[style.id]}
                    onClick={() => handleCloneStyle(style.id, style.name)}
                  >
                    {isCloning[style.id] ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                    复制到企业
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => {
                      if (confirm(`确定要删除风格模板「${style.name}」吗？`)) {
                        deleteStyle(style.id)
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <NoteStyleEditor
        styleId={currentStyleId}
        open={styleEditorOpen}
        onClose={() => {
          setStyleEditorOpen(false)
          setCurrentStyleId(null)
        }}
        overrideOrgId={SYSTEM_TEMPLATE_ORG_ID}
      />
    </div>
  )
}
